import React, { createContext, useContext, useState, useEffect } from 'react';
import { vendorApi } from './api';

const VendorContext = createContext();

export const useVendor = () => {
  const context = useContext(VendorContext);
  if (!context) {
    throw new Error('useVendor must be used within a VendorProvider');
  }
  return context;
};

export const VendorProvider = ({ children }) => {
  const [vendor, setVendor] = useState(null);
  const [vendorStats, setVendorStats] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Login vendor with vendor data (for vendor ID login)
  const login = async (vendorData) => {
    setLoading(true);
    setError('');
    
    try {
      // Set vendor data directly
      setVendor(vendorData);
      
      // Store vendor info
      localStorage.setItem('vendorId', vendorData.id.toString());
      localStorage.setItem('vendorEmail', vendorData.email || '');
      localStorage.setItem('isVendor', 'true');
      
      // Load additional vendor data if needed
      await loadVendorData(vendorData.id);
      
      return vendorData;
    } catch (err) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Login vendor (legacy email/password method)
  const loginVendor = async (email, password) => {
    setLoading(true);
    setError('');
    
    try {
      // Use the vendor auth endpoint
      const response = await vendorApi.login(email, password);
      
      if (!response.success) {
        throw new Error(response.message || 'Login failed');
      }

      const foundVendor = response.vendor;

      // Store vendor info and tokens
      setVendor(foundVendor);
      localStorage.setItem('vendorId', foundVendor.id.toString());
      localStorage.setItem('vendorEmail', foundVendor.email);
      localStorage.setItem('isVendor', 'true');
      
      // Store JWT tokens for authentication
      if (response.access_token) {
        localStorage.setItem('access_token', response.access_token);
      }
      if (response.refresh_token) {
        localStorage.setItem('refresh_token', response.refresh_token);
      }

      // Load vendor data
      await loadVendorData(foundVendor.id);
      
      return foundVendor;
    } catch (err) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Load vendor data by ID
  const loadVendorData = async (vendorId) => {
    try {
      setLoading(true);
      
      // Load vendor profile
      const vendorProfile = await vendorApi.getProfile(vendorId);
      setVendor(vendorProfile);

      // Load vendor stats
      const stats = await vendorApi.getVendorStats(vendorId);
      setVendorStats(stats);

      // Load vendor bids
      const vendorBids = await vendorApi.getVendorBids(vendorId);
      setBids(vendorBids);

    } catch (err) {
      setError(err.message || 'Failed to load vendor data');
    } finally {
      setLoading(false);
    }
  };

  // Load vendor data by email (for main auth system integration)
  const loadVendorDataByEmail = async (email) => {
    try {
      setLoading(true);
      console.log('Loading vendor by email:', email);
      
      // First, get vendor profile by email
      const vendorProfile = await vendorApi.getProfileByEmail(email);
      console.log('Found vendor profile:', vendorProfile);
      
      if (!vendorProfile) {
        setError('Vendor profile not found. Please contact administrator.');
        return;
      }
      
      setVendor(vendorProfile);

      // Load vendor stats
      const stats = await vendorApi.getVendorStats(vendorProfile.id);
      setVendorStats(stats);

      // Load vendor bids
      const vendorBids = await vendorApi.getVendorBids(vendorProfile.id);
      setBids(vendorBids);

      // Update localStorage with vendor ID for future use
      localStorage.setItem('vendorId', vendorProfile.id.toString());

    } catch (err) {
      console.error('Failed to load vendor data by email:', err);
      setError(err.message || 'Failed to load vendor data');
    } finally {
      setLoading(false);
    }
  };

  // Refresh vendor data
  const refreshVendorData = async () => {
    if (vendor?.id) {
      console.log('🔍 VendorContext: Refreshing vendor data for ID:', vendor.id);
      await loadVendorData(vendor.id);
    } else if (vendor?.email) {
      console.log('🔍 VendorContext: Refreshing vendor data by email:', vendor.email);
      await loadVendorDataByEmail(vendor.email);
    }
  };

  // Create new bid
  const createBid = async (bidData) => {
    try {
      setLoading(true);
      const newBid = await vendorApi.createBid({
        ...bidData,
        vendorId: vendor.id
      });
      
      // Refresh data to get updated stats and bid list
      await refreshVendorData();
      
      return newBid;
    } catch (err) {
      setError(err.message || 'Failed to create bid');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update bid
  const updateBid = async (bidId, bidData) => {
    try {
      setLoading(true);
      const updatedBid = await vendorApi.updateBid(bidId, bidData);
      
      // Refresh data
      await refreshVendorData();
      
      return updatedBid;
    } catch (err) {
      setError(err.message || 'Failed to update bid');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update bid status
  const updateBidStatus = async (bidId, status, reason = '') => {
    try {
      setLoading(true);
      const updatedBid = await vendorApi.updateBidStatus(bidId, status, reason);
      
      // Refresh data
      await refreshVendorData();
      
      return updatedBid;
    } catch (err) {
      setError(err.message || 'Failed to update bid status');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout vendor
  const logoutVendor = () => {
    // Clear all vendor state
    setVendor(null);
    setVendorStats(null);
    setBids([]);
    setError('');
    setLoading(false);
    
    // Clear all authentication and vendor data from localStorage
    localStorage.removeItem('vendorId');
    localStorage.removeItem('vendorEmail');
    localStorage.removeItem('isVendor');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('role');
    localStorage.removeItem('customerId');
    
    // Clear any vendor-specific cache or draft data
    localStorage.removeItem('premiumBidDraft');
    localStorage.removeItem('bidDraft');
    
    // Clear sessionStorage as well
    sessionStorage.clear();
    
    console.log('🔓 Vendor logout completed - all data cleared');
  };

  // Check if user is logged in as vendor on mount
  useEffect(() => {
    const vendorId = localStorage.getItem('vendorId');
    const vendorEmail = localStorage.getItem('vendorEmail');
    const isVendor = localStorage.getItem('isVendor');
    const role = localStorage.getItem('role');
    
    console.log('VendorContext useEffect:', { vendorId, vendorEmail, isVendor, role });
    
    // Handle both cases: direct vendor login and main auth vendor login
    if ((vendorId && isVendor === 'true') || (role === 'Vendor' && vendorEmail)) {
      if (vendorId) {
        console.log('Loading vendor data by ID:', vendorId);
        loadVendorData(parseInt(vendorId));
      } else if (vendorEmail) {
        console.log('Loading vendor data by email:', vendorEmail);
        loadVendorDataByEmail(vendorEmail);
      }
    }
  }, []);

  const value = {
    vendor,
    vendorStats,
    bids,
    loading,
    error,
    login,
    loginVendor,
    loadVendorData,
    refreshVendorData,
    createBid,
    updateBid,
    updateBidStatus,
    logoutVendor,
    setError
  };

  return (
    <VendorContext.Provider value={value}>
      {children}
    </VendorContext.Provider>
  );
};

export default VendorContext;